import { type JSXElement } from 'solid-js'
import { createStore } from 'solid-js/store'

const [dialog, setDialog] = createStore<{
  isOpen: boolean
  isLoading: boolean
  title: string
  description: string | (() => JSXElement)
  descriptionClass?: string
  primaryButtonText: string
  secondaryButtonText?: string
  /** Two-button arrangement: side-by-side row (default) or primary-on-top
   *  stacked, where the secondary renders as a borderless text button. */
  buttonLayout?: 'row' | 'stacked'
  primaryAction?: () => void
  /** Like primaryAction, but deferred until the dialog has fully exited
   *  (content unmounted, body styles restored). Required when the action
   *  opens another modal overlay (e.g. the Boosting Packs sheet): opening it
   *  while the dialog is still mounted makes the two body-style locks
   *  snapshot each other and the page is left with `pointer-events: none`. */
  primaryActionAfterExit?: () => void
  asyncPrimaryAction?: () => Promise<void>
  secondaryAction?: () => void
}>({
  isOpen: false,
  isLoading: false,
  title: '',
  description: '',
  primaryButtonText: '',
})

export { dialog }

let pendingAfterExitAction: (() => void) | undefined

/** Stashes an action to run once the dialog content has unmounted. */
export const queueDialogAfterExitAction = (action: () => void) => {
  pendingAfterExitAction = action
}

/**
 * Called from the dialog content's onCleanup. The setTimeout pushes the
 * action past the whole dispose pass, so it runs only after the dialog's
 * body-style locks (pointer-events, scroll) have been released.
 */
export const flushDialogAfterExitAction = () => {
  const action = pendingAfterExitAction
  pendingAfterExitAction = undefined
  if (action) setTimeout(action, 0)
}

export const closeDialog = () => {
  setDialog({ isOpen: false, isLoading: false })
}

export const setDialogLoading = (isLoading: boolean) => {
  setDialog('isLoading', isLoading)
}

export const showDialog = (payload: {
  title: string
  description: string | (() => JSXElement)
  descriptionClass?: string
  primaryButtonText: string
  secondaryButtonText?: string
  buttonLayout?: 'row' | 'stacked'
  primaryAction?: () => void
  primaryActionAfterExit?: () => void
  asyncPrimaryAction?: () => Promise<void>
  secondaryAction?: () => void
}) => {
  setDialog({
    isOpen: true,
    isLoading: false,
    descriptionClass: undefined,
    secondaryButtonText: undefined,
    buttonLayout: undefined,
    primaryAction: undefined,
    primaryActionAfterExit: undefined,
    asyncPrimaryAction: undefined,
    secondaryAction: undefined,
    ...payload,
  })
}
